import { useParams, Link } from "react-router-dom";
import { 
  ArrowLeft, 
  Phone, 
  Clock, 
  Share2, 
  CheckCircle,
  ArrowRight,
  MapPin,
  AlertTriangle,
  TrendingUp
} from "lucide-react";
import { Button } from "@/components/ui/button";
import useSEO from "@/hooks/useSEO";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

// Article content database
const ARTICLES_CONTENT = {
  "minnesota-storm-damage-statistics": {
    title: "Minnesota Storm Damage Statistics Every Homeowner Should Know",
    category: "Storm Statistics",
    readTime: "8 min read",
    intro: "Minnesota has experienced significant severe weather over the past several decades. Understanding these statistics helps homeowners recognize the real risks and take appropriate action to protect their properties.",
    sections: [
      {
        heading: "38 Billion-Dollar Severe Storm Events Since 1980",
        content: `According to the NOAA National Centers for Environmental Information, Minnesota has experienced 38 billion-dollar severe storm events since 1980. These events represent storms where total damages exceeded one billion dollars, accounting for inflation.

This frequency means Minnesota homeowners face a higher-than-average risk of significant storm damage compared to many other states. The combination of geographic location, weather patterns, and population density creates conditions where severe storms regularly impact residential properties.

For homeowners, this data underscores the importance of understanding your insurance coverage, maintaining your roof and exterior, and knowing what to do when storms strike.`
      },
      {
        heading: "Severe Storms Represent 61% Of Major Weather Losses",
        content: `More than 61% of Minnesota's billion-dollar weather disasters are classified as severe storm events. This category includes thunderstorms, hail, tornadoes, and high wind events - the types of weather that most commonly damage roofing systems, siding, and gutters.

Unlike flooding or drought, severe storm damage often occurs suddenly and without warning. A single afternoon thunderstorm can produce hail large enough to damage shingles, wind strong enough to lift roofing materials, and conditions that lead to fallen trees and debris impact.

This is why post-storm inspections are so important. Even if damage isn't immediately visible, the cumulative effect of multiple storm events can significantly reduce your roof's remaining lifespan.`
      },
      {
        heading: "Storm Frequency Has Accelerated",
        content: `Weather data shows that severe storm frequency in Minnesota has increased over recent decades. What was once considered a "once in a decade" storm event now occurs more regularly in many parts of the state.

For homeowners, this trend means:
• Regular property inspections after severe weather are more important than ever
• Insurance coverage should be reviewed annually to ensure adequate protection
• Building relationships with reputable contractors before you need them saves time during emergencies
• Understanding your deductible and policy details helps you make informed decisions quickly`
      },
      {
        heading: "Why These Numbers Matter For Your Home",
        content: `Statistics aren't just abstract numbers - they represent real damage to real homes. When you understand that Minnesota regularly experiences billion-dollar storm events, you can better appreciate why:

• Professional roof inspections after significant storms are worthwhile investments
• Working with established, local contractors provides better protection than choosing the lowest bidder
• Proper documentation of existing conditions helps with potential insurance claims
• Proactive maintenance extends the life of your roofing system

Being prepared isn't about fear - it's about making informed decisions that protect your investment.`
      }
    ],
    faqs: [
      { q: "How often does Minnesota experience major storms?", a: "Minnesota averages nearly one billion-dollar severe storm event per year since 1980, though frequency has increased in recent years." },
      { q: "What types of damage do these storms cause?", a: "The majority involve hail, wind, and thunderstorm damage affecting roofs, siding, gutters, and exterior surfaces." },
      { q: "Should I inspect my roof after every storm?", a: "Visual inspection from the ground is recommended after any significant weather event. Professional inspection is advisable after storms with reported hail or high winds." }
    ],
    relatedCities: ["minnetonka", "eden-prairie", "maple-grove", "blaine"]
  },
  "wind-damage-vs-hail-damage": {
    title: "Wind Damage vs. Hail Damage: What's More Common In Minnesota?",
    category: "Storm Damage",
    readTime: "7 min read",
    intro: "Many homeowners assume storm damage means hail damage. While hail is certainly a major concern in Minnesota, wind damage is equally common and often more difficult to detect. Understanding both types of damage helps you identify problems before they worsen.",
    sections: [
      {
        heading: "Wind Damage: The Often-Overlooked Threat",
        content: `Wind damage is frequently underestimated because it's less visible than the obvious dents left by hail. However, high winds can cause serious roofing problems including:

• Lifted or curled shingles that no longer provide proper protection
• Creased shingles from wind uplift that remain in place but are structurally compromised
• Missing shingles that may have blown away during the storm
• Damaged ridge caps along the peak of the roof
• Compromised seals around vents, skylights, and other penetrations

Wind damage often starts at vulnerable points - edges, corners, and ridgelines - before affecting the broader roof surface. A roof may appear intact while having significant hidden damage.`
      },
      {
        heading: "Hail Damage: Visible and Hidden Signs",
        content: `Hail damage ranges from obvious dents to subtle bruising that only professionals can identify. Signs include:

• Dented or cracked shingles visible from the ground
• Granule loss appearing as dark spots or exposed fiberglass mat
• Soft spots or bruises that compromise shingle integrity
• Damage to metal components like vents, flashing, and gutters
• Cracked or chipped siding and window trim

Even small hail (1 inch diameter or less) can cause damage, particularly to aging roofing materials. The damage may not leak immediately but significantly reduces the roof's remaining functional life.`
      },
      {
        heading: "Combined Storm Events",
        content: `Minnesota storms frequently produce both wind and hail simultaneously. These combined events create compound damage that can be particularly challenging to assess:

• Hail impacts can loosen shingles, making them more susceptible to wind uplift
• Wind-lifted shingles expose the underlayment to hail damage
• Debris carried by wind can puncture or tear roofing materials
• The cumulative effect often exceeds the damage either would cause alone

This is why professional assessment after significant storms is valuable - trained inspectors can identify the full extent of damage that homeowners might miss.`
      },
      {
        heading: "What Homeowners Often Miss",
        content: `Common misconceptions lead homeowners to overlook damage:

• "If it's not leaking, it's fine" - Damage can exist for months before causing visible leaks
• "Hail wasn't large enough to matter" - Even small hail damages roofing materials
• "I don't see anything from the ground" - Many damage types are only visible during roof inspection
• "The storm wasn't that bad" - Localized conditions can vary significantly even within a neighborhood

When in doubt, professional inspection provides peace of mind and documentation that may be valuable if issues develop later.`
      }
    ],
    faqs: [
      { q: "Which is more common in Minnesota - wind or hail damage?", a: "Both are common. Hail damage tends to be more discussed, but wind damage occurs with nearly every severe thunderstorm." },
      { q: "Can wind damage happen without hail?", a: "Yes. Many storms produce damaging winds without significant hail. Straight-line winds from thunderstorms regularly exceed 60 mph in Minnesota." },
      { q: "How can I tell if I have wind damage?", a: "Look for missing shingles in your yard, curled or lifted shingles visible from the ground, and debris accumulation in gutters. Professional inspection can identify hidden damage." }
    ],
    relatedCities: ["plymouth", "brooklyn-park", "coon-rapids", "andover"]
  },
  "how-to-avoid-storm-chasers": {
    title: "How To Avoid Storm Chasers After A Minnesota Storm",
    category: "Contractor Selection",
    readTime: "6 min read",
    intro: "After major storms, contractors flood affected areas looking for business. While some are legitimate professionals, others are 'storm chasers' - contractors who travel from storm to storm with little accountability to the communities they work in. Here's how to protect yourself.",
    sections: [
      {
        heading: "What Are Storm Chasers?",
        content: `Storm chasers are contractors who travel to areas affected by severe weather, often from out of state, seeking quick business. While not all traveling contractors are problematic, the storm chaser business model creates inherent risks:

• Little local accountability or reputation to protect
• May not be familiar with local building codes
• Often here today, gone tomorrow
• Difficult to contact if problems arise after the job
• May use high-pressure sales tactics to close quickly

The best defense is knowing what to look for and asking the right questions before signing any contract.`
      },
      {
        heading: "Red Flags To Watch For",
        content: `Protect yourself by recognizing these warning signs:

• Door-to-door solicitation immediately after storms
• Pressure to sign contracts the same day
• Requests for large upfront payments or full payment before work begins
• Out-of-state license plates on work vehicles
• No local office or permanent address
• Reluctance to provide references from your area
• Vague or missing information about licensing and insurance
• Offers that seem too good to be true
• Push to have you sign an Assignment of Benefits document immediately`
      },
      {
        heading: "Questions To Ask Any Contractor",
        content: `Before hiring anyone, ask these important questions:

• How long have you been working in Minnesota?
• Can you provide your Minnesota contractor license number?
• Do you have a local office I can visit?
• Can you provide recent local references I can contact?
• Will you provide a detailed written estimate before starting work?
• What is your payment schedule?
• What warranties do you offer?
• How do you handle problems that arise after completion?

Reputable contractors welcome these questions. Anyone who becomes defensive or evasive should raise concerns.`
      },
      {
        heading: "Working With Established Local Contractors",
        content: `The safest approach is working with contractors who have an established presence in Minnesota:

• They have local reputations to protect
• They're familiar with Minnesota weather conditions and building requirements
• They'll be here next year if issues arise
• They often have relationships with local suppliers and building officials
• Their work can be verified through local references

Taking time to find the right contractor is always worth it - even when you're dealing with the stress of storm damage.`
      }
    ],
    faqs: [
      { q: "Are all out-of-state contractors storm chasers?", a: "No, but out-of-state contractors require extra vetting. Verify licensing, insurance, and references carefully before hiring anyone without a local track record." },
      { q: "Is it bad to get estimates from contractors who knock on my door?", a: "Getting estimates is fine, but never feel pressured to decide immediately. Take time to verify credentials and compare with established local contractors." },
      { q: "What if a storm chaser already started work on my roof?", a: "Document everything, contact your insurance company, and consult with a local attorney if necessary. Do not make additional payments until work quality is verified." }
    ],
    relatedCities: ["chanhassen", "ham-lake", "forest-lake", "elk-river"]
  },
  "signs-your-roof-has-storm-damage": {
    title: "Signs Your Roof May Have Storm Damage",
    category: "Storm Damage",
    readTime: "7 min read",
    intro: "After severe weather, knowing what to look for can help you identify potential storm damage before it leads to bigger problems. Here's a comprehensive guide to the most common signs of storm damage on Minnesota homes.",
    sections: [
      {
        heading: "Wind Damage Indicators",
        content: `High winds can damage roofs in several ways. Look for:

• Missing shingles - Check your yard and neighboring areas for shingles that may have blown off
• Lifted or curled shingle edges - Wind can break the seal and lift shingles, creating entry points for water
• Creased or bent shingles - Even shingles that remain in place may be damaged
• Exposed underlayment - Dark spots where shingles should be indicate missing or displaced materials
• Debris accumulation - Branches, leaves, and other debris may indicate high wind conditions that could have caused damage

Wind damage often starts at vulnerable points like roof edges, ridge lines, and areas around vents or skylights.`
      },
      {
        heading: "Hail Damage Signs",
        content: `Hail damage varies based on hail size and roofing material. Common indicators include:

• Dents or bruises on shingles - May appear as dark spots or soft areas
• Granule loss - Look for bare spots on shingles or accumulation of granules in gutters
• Cracked or split shingles - Larger hail can cause visible cracking
• Damage to metal components - Check vents, flashing, and gutters for dents
• Damage to other surfaces - Dented siding, damaged window screens, or marks on air conditioning units suggest hail may have also damaged your roof

Even if you can't see damage from the ground, hail that damages other surfaces likely impacted your roof as well.`
      },
      {
        heading: "Interior Warning Signs",
        content: `Sometimes the first signs of roof damage appear inside your home:

• Water stains on ceilings - Yellow or brown stains indicate water infiltration
• Damp insulation in the attic - Check after rain for moisture that shouldn't be there
• Light visible through the roof - If you can see daylight from your attic, you have a problem
• Musty odors - Can indicate hidden moisture and potential mold growth
• Peeling paint on ceiling or walls - May result from moisture migration

Interior damage often indicates a problem that's been developing for some time. Prompt attention prevents more extensive repairs.`
      },
      {
        heading: "What To Do Next",
        content: `If you notice any of these signs:

1. Document what you see with photos and notes, including the date
2. Do not climb on your roof - leave inspection to professionals
3. Contact your insurance company if damage is significant
4. Get a professional inspection from a reputable local contractor
5. Don't feel pressured to make immediate decisions

Remember that some damage isn't visible from the ground. Professional inspection provides a complete assessment and documentation that may be valuable for insurance claims.`
      }
    ],
    faqs: [
      { q: "Should I climb on my roof to check for damage?", a: "No. Roof inspection should be left to professionals for safety reasons. You can observe from the ground or from a safe vantage point." },
      { q: "How long after a storm should I look for damage?", a: "Initial visual inspection from the ground should happen within 24-48 hours. Professional inspection should be scheduled within a week of significant storms." },
      { q: "What if I find damage - do I need to file a claim immediately?", a: "Document the damage promptly, but you typically have time to file. Review your policy or contact your agent to understand your specific timeline requirements." }
    ],
    relatedCities: ["rogers", "monticello", "big-lake", "cambridge"]
  }
};

// Default content for articles not yet written
const DEFAULT_ARTICLE = {
  title: "Article Coming Soon",
  category: "Storm Damage",
  readTime: "5 min read",
  intro: "This article is being developed by our team. Check back soon for valuable information about storm damage, insurance claims, and contractor selection in Minnesota.",
  sections: [
    {
      heading: "Content In Development",
      content: "We're working on bringing you comprehensive, helpful content on this topic. In the meantime, explore our other resources or contact us directly with your questions."
    }
  ],
  faqs: [],
  relatedCities: ["minnetonka", "eden-prairie", "plymouth"]
};

export default function ArticlePage() {
  const { slug } = useParams();
  const article = ARTICLES_CONTENT[slug] || { ...DEFAULT_ARTICLE, title: slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') };

  useSEO();

  // Schema markup
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.intro,
    "author": {
      "@type": "Organization",
      "name": "Minnesota Storm Damage Resource Center"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Minnesota Storm Damage Resource Center"
    }
  };

  const faqSchema = article.faqs?.length ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": article.faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  } : null;

  return (
    <div data-testid={`article-page-${slug}`} className="">
      {/* Schema Markup */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      {faqSchema && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />}

      {/* Header */}
      <section className="bg-navy py-12">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <Breadcrumb className="mb-6">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/" className="text-gray-400 hover:text-white">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="text-gray-600" />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/resources" className="text-gray-400 hover:text-white">Resources</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="text-gray-600" />
              <BreadcrumbItem>
                <BreadcrumbPage className="text-white">{article.category}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center gap-4 mb-4">
            <span className="bg-orange/20 text-orange text-sm font-medium px-3 py-1 rounded-full">
              {article.category}
            </span>
            <span className="text-gray-400 text-sm flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {article.readTime}
            </span>
          </div>

          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-white tracking-tight leading-tight">
            {article.title}
          </h1>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          {/* Intro */}
          <p className="text-xl text-gray-600 leading-relaxed mb-12 border-l-4 border-orange pl-6">
            {article.intro}
          </p>

          {/* Sections */}
          <div className="prose prose-lg max-w-none">
            {article.sections.map((section) => (
              <div key={section.heading} className="mb-12">
                <h2 className="font-heading text-2xl font-semibold text-navy mb-4">
                  {section.heading}
                </h2>
                <div className="text-gray-600 leading-relaxed whitespace-pre-line">
                  {section.content}
                </div>
              </div>
            ))}
          </div>

          {/* FAQs */}
          {article.faqs?.length > 0 && (
            <div className="mt-16 bg-gray-50 rounded-2xl p-8">
              <h2 className="font-heading text-2xl font-semibold text-navy mb-6">
                Frequently Asked Questions
              </h2>
              <div className="space-y-6">
                {article.faqs.map((faq) => (
                  <div key={faq.q}>
                    <h3 className="font-heading font-semibold text-navy mb-2">{faq.q}</h3>
                    <p className="text-gray-600">{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-16 bg-navy rounded-2xl p-8 text-center">
            <h3 className="font-heading text-2xl font-semibold text-white mb-3">
              Have Questions About Storm Damage?
            </h3>
            <p className="text-gray-400 mb-6">
              Connect with a trusted Minnesota professional who can help assess your situation.
            </p>
            <Link to="/#lead-form">
              <Button className="bg-orange hover:bg-orange-hover text-white px-8 py-4">
                <Phone className="w-5 h-5 mr-2" />
                Get Connected
              </Button>
            </Link>
          </div>

          {/* Related Cities */}
          {article.relatedCities?.length > 0 && (
            <div className="mt-12">
              <h3 className="font-heading text-lg font-semibold text-navy mb-4">
                Related Service Areas
              </h3>
              <div className="flex flex-wrap gap-2">
                {article.relatedCities.map(citySlug => (
                  <Link
                    key={citySlug}
                    to={`/service-areas/${citySlug}`}
                    className="inline-flex items-center gap-1 bg-gray-100 hover:bg-orange/10 text-navy hover:text-orange px-3 py-2 rounded-lg text-sm transition-colors"
                  >
                    <MapPin className="w-3 h-3" />
                    {citySlug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>

      {/* Back to Resources */}
      <section className="py-8 bg-gray-50 border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <Link 
            to="/resources"
            className="inline-flex items-center text-navy hover:text-orange font-medium"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Resource Center
          </Link>
        </div>
      </section>
    </div>
  );
}
